import React, { useState } from "react";
import { toast } from "sonner";
import {
  Gift, Users, Trophy, ShieldCheck, Copy, Download, Share2, KeyRound, Check, Sparkles,
} from "lucide-react";
import api, { apiErr } from "../lib/api";

const ACH = [
  { n: 10, reward: 1 },
  { n: 50, reward: 2 },
  { n: 100, reward: 3 },
];

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="luma-card p-4 mb-4">
      <div className="flex items-center gap-2.5 mb-2">
        <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center">
          <Icon size={18} />
        </span>
        <div>
          <h3 className="font-display font-bold text-base leading-tight">{title}</h3>
          {subtitle && <p className="text-xs luma-muted font-semibold">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function BonusScreen({ state, onClaim, refreshState }) {
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const inviteLink = `${origin}/?ref=${state?.referral_code || ""}`;
  const shareLink = state?.share_token ? `${origin}/share/${state.share_token}` : null;

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copiato!`);
  };

  const genRecovery = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/recovery/generate");
      setRecovery(data.recovery_code);
      refreshState();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadRecovery = () => {
    const blob = new Blob([`Lumacofono — codice di recupero\n\n${recovery}\n\nConserva questo codice: serve a ritrovare la tua collezione su un altro browser.`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "lumacofono-recupero.txt";
    a.click();
  };

  const exportCollection = async () => {
    try {
      const { data } = await api.get("/collection/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "lumacofono-collezione.json";
      a.click();
      toast.success("Collezione esportata!");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleShare = async () => {
    try {
      const { data } = await api.post("/share/toggle");
      refreshState();
      toast.success(data.share_enabled ? "Condivisione attivata" : "Condivisione disattivata");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const opened = state?.packs_opened || 0;

  return (
    <div className="px-4 pt-4" data-testid="screen-bonus">
      <h2 className="font-display font-bold text-2xl mb-3">Bonus &amp; Premi</h2>

      <Section icon={Gift} title="Bonus giornaliero" subtitle="1 bustina bonus + 1 carta collezione al giorno (ora di Roma)">
        <button
          onClick={onClaim}
          disabled={state?.daily_claimed_today}
          data-testid="claim-daily-btn"
          className="pill-primary w-full py-3.5 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
        >
          {state?.daily_claimed_today ? (<><Check size={18} /> Riscattato oggi</>) : (<><Sparkles size={18} /> Riscatta ora</>)}
        </button>
        <p className="text-xs luma-muted font-semibold mt-2 text-center">
          Le bustine bonus si conservano separatamente: <b>{state?.bonus_balance ?? 0}</b> disponibili.
        </p>
      </Section>

      <Section icon={Users} title="Invita un amico" subtitle="Ricevi +1 bustina bonus quando un nuovo amico apre la sua prima bustina (max 3 al giorno)">
        <div className="flex gap-2">
          <input readOnly value={inviteLink} className="flex-1 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 text-xs font-mono outline-none" data-testid="invite-link" />
          <button onClick={() => copy(inviteLink, "Link invito")} data-testid="copy-invite-btn" className="pill-soft px-4 flex items-center gap-1.5 text-sm">
            <Copy size={15} /> Copia
          </button>
        </div>
      </Section>

      <Section icon={Trophy} title="Obiettivi" subtitle={`Bustine aperte: ${opened}`}>
        <div className="space-y-2">
          {ACH.map((a) => {
            const done = opened >= a.n;
            const prog = Math.min(100, Math.round((opened / a.n) * 100));
            return (
              <div key={a.n} data-testid={`achievement-${a.n}`} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-xs font-bold mb-1">
                    <span>Apri {a.n} bustine</span>
                    <span className={done ? "text-emerald-600" : "luma-muted"}>+{a.reward} bonus {done ? "✓" : ""}</span>
                  </div>
                  <div className="h-2 rounded-full bg-sky-100 overflow-hidden">
                    <div className={`h-full ${done ? "bg-emerald-400" : "bg-gradient-to-r from-purple-400 to-pink-400"}`} style={{ width: `${prog}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section icon={KeyRound} title="Codice di recupero" subtitle="La collezione è collegata a questo browser. Salva il codice per non perderla.">
        {recovery ? (
          <div data-testid="recovery-code-box">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 font-mono text-sm break-all">{recovery}</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => copy(recovery, "Codice")} className="pill-soft flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5"><Copy size={15} /> Copia</button>
              <button onClick={downloadRecovery} className="pill-soft flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5"><Download size={15} /> Scarica</button>
            </div>
            <p className="text-[11px] luma-muted mt-2 font-semibold">Mostrato una sola volta. Conservalo in un posto sicuro.</p>
          </div>
        ) : (
          <button onClick={genRecovery} disabled={busy} data-testid="generate-recovery-btn" className="pill-primary w-full py-3 text-sm">
            {state?.has_recovery ? "Genera un nuovo codice" : "Genera codice di recupero"}
          </button>
        )}
      </Section>

      <Section icon={Share2} title="Condividi &amp; Esporta" subtitle="Link di sola lettura e backup locale della collezione">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold">Collezione pubblica</span>
          <button onClick={toggleShare} data-testid="share-toggle-btn" className={`chip ${state?.share_enabled ? "bg-emerald-500 text-white" : "bg-white border-sky-200"}`}>
            {state?.share_enabled ? "Attiva" : "Disattivata"}
          </button>
        </div>
        {shareLink && (
          <div className="flex gap-2 mb-3">
            <input readOnly value={shareLink} className="flex-1 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 text-xs font-mono outline-none" data-testid="share-link" />
            <button onClick={() => copy(shareLink, "Link")} className="pill-soft px-4 flex items-center gap-1.5 text-sm"><Copy size={15} /></button>
          </div>
        )}
        <button onClick={exportCollection} data-testid="export-btn" className="pill-soft w-full py-2.5 text-sm flex items-center justify-center gap-1.5">
          <Download size={15} /> Esporta collezione (JSON)
        </button>
      </Section>

      <div className="luma-card p-3.5 mb-4 flex gap-2.5 bg-sky-50/70">
        <ShieldCheck size={18} className="text-sky-500 shrink-0 mt-0.5" />
        <p className="text-[12px] luma-muted font-semibold">
          Tutto è salvato sul server e collegato a questo browser tramite un cookie sicuro. Timer, estrazioni e premi sono gestiti dal server: ricaricare la pagina o usare più schede non duplica i premi.
        </p>
      </div>
    </div>
  );
}
